import { useCallback, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog";
import { FindingEvidence } from "@/features/audit/FindingEvidence";
import type { EvidenceItem } from "@/features/audit/types";
import { useEvidencePhotoUrls } from "../useEvidencePhotoUrls";

type Photo = Extract<EvidenceItem, { kind: "photo" }>;

/**
 * Learning-card evidence. Clips keep the shared player; photos render ONE at a
 * time inside a uniform 16:9 frame (object-contain — cropping could hide the
 * very defect being judged), with Instagram-style multiplicity hints: side
 * arrows, a "2 / 7" counter, dots, and swipe. Click (or the expand button)
 * opens a fullscreen view with the same navigation.
 */
export function EvidenceDisplay({
  items,
  propertyId,
}: {
  items: EvidenceItem[] | undefined;
  /** The card's property — routes clip playback through the client-authorized
   * /v1/learning/video endpoint (see FindingEvidence). */
  propertyId?: string;
}) {
  // A queue card can arrive with no evidence (empty finding, or a backend that
  // doesn't populate it inline) — degrade to "nothing to show" instead of
  // crashing the whole Learning page on `undefined.filter`.
  const list = items ?? [];
  const clips = list.filter((i): i is Extract<EvidenceItem, { kind: "clip" }> => i.kind === "clip");
  const photos = list.filter((i): i is Photo => i.kind === "photo");
  if (clips.length === 0 && photos.length === 0) {
    return <p className="text-xs text-muted-foreground">No captured evidence linked yet.</p>;
  }
  return (
    <div className="space-y-2">
      {clips.length > 0 && <FindingEvidence items={clips} learningPropertyId={propertyId} />}
      {photos.length > 0 && <PhotoCarousel photos={photos} />}
    </div>
  );
}

function PhotoCarousel({ photos }: { photos: Photo[] }) {
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);
  const touchX = useRef<number | null>(null);
  const many = photos.length > 1;
  const safeIdx = Math.min(idx, photos.length - 1);
  const photo = photos[safeIdx];
  // Resolve raw file_path -> displayable URL (sign storage keys, pass through
  // http(s)): undefined = resolving, null = failed, string = ready.
  const resolvedUrls = useEvidencePhotoUrls(photos.map((p) => p.url));
  const displayUrl = resolvedUrls[safeIdx];

  const step = useCallback(
    (dir: -1 | 1) => setIdx((i) => Math.min(photos.length - 1, Math.max(0, i + dir))),
    [photos.length],
  );

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
  };

  const arrows = (cls = "") =>
    many && (
      <>
        {idx > 0 && (
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            className={`absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/70 p-1.5 shadow backdrop-blur transition-colors hover:bg-background ${cls}`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {idx < photos.length - 1 && (
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/70 p-1.5 shadow backdrop-blur transition-colors hover:bg-background ${cls}`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </>
    );

  const counter = many && (
    <span className="absolute right-2 top-2 rounded-full bg-background/70 px-2 py-0.5 text-xs font-medium backdrop-blur">
      {idx + 1} / {photos.length}
    </span>
  );

  return (
    <figure className="space-y-1.5">
      <div
        className="relative aspect-video w-full overflow-hidden rounded-md bg-muted/30 ring-1 ring-border/30"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={photo.caption ?? "Evidence"}
            loading="lazy"
            onClick={() => setFull(true)}
            className="h-full w-full cursor-zoom-in object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            {displayUrl === null ? "Image unavailable" : "Loading image…"}
          </div>
        )}
        {arrows()}
        {counter}
        {/* Expand only when there's actually an image to open. */}
        {displayUrl && (
          <button
            type="button"
            aria-label="View fullscreen"
            onClick={() => setFull(true)}
            className="absolute bottom-2 right-2 rounded-full bg-background/70 p-1.5 shadow backdrop-blur transition-colors hover:bg-background"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {many && (
        <div className="flex items-center justify-center gap-1.5">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Photo ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${i === idx ? "bg-foreground" : "bg-muted-foreground/40 hover:bg-muted-foreground/70"}`}
            />
          ))}
        </div>
      )}
      {photo.caption && <figcaption className="text-center text-xs text-muted-foreground">{photo.caption}</figcaption>}

      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent
          className="max-w-[96vw] border-none bg-transparent p-0 shadow-none sm:rounded-none"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") step(-1);
            if (e.key === "ArrowRight") step(1);
          }}
        >
          <DialogTitle className="sr-only">{photo.caption ?? "Evidence photo"}</DialogTitle>
          <div className="relative">
            {displayUrl && (
              <img
                src={displayUrl}
                alt={photo.caption ?? "Evidence"}
                className="mx-auto max-h-[88vh] w-auto max-w-full rounded-md object-contain"
              />
            )}
            {arrows()}
            {counter}
            {photo.caption && (
              <p className="mt-2 text-center text-sm text-muted-foreground">{photo.caption}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </figure>
  );
}
